<template>
  <v-form ref="form" v-model="valid" validate-on="input lazy">
    <v-select
      v-model="localValue.os"
      :items="osOptions"
      :rules="[required()]"
      :label="$t('devicesSettings.devicePolicyTab.form.operatingSystem')"
      variant="outlined"
      item-title="title"
      item-value="value"
      :placeholder="$t('devicesSettings.devicePolicyTab.form.selectOperatingSystem')"
      :hint="windowsServerHint"
      persistent-hint
      data-testid="fim-os-select"
    >
      <template #message="{ message }">
        <span class="d-flex align-center">
          <v-icon icon="$infoXs" size="18" class="mr-1" data-testid="fim-os-hint-icon" />
          {{ message }}
        </span>
      </template>
    </v-select>

    <div class="subtitle2 mb-2 mt-6">{{ $t("devicesSettings.devicePolicyTab.form.action") }}</div>
    <v-radio-group v-model="localValue.action" hide-details class="mb-4" data-testid="fim-action">
      <v-radio
        v-for="option in actionOptions"
        :key="option.value"
        :label="option.title"
        :value="option.value"
      />
    </v-radio-group>

    <div class="subtitle1">
      {{ $t("devicesSettings.devicePolicyTab.form.verificationInterval") }}
    </div>
    <div class="body2 text-indigo-medium mb-7">
      {{ $t("devicesSettings.devicePolicyTab.form.verificationIntervalHelper") }}
    </div>
    <v-row class="mb-6" data-testid="fim-interval">
      <v-col class="key-part" cols="6" md="6">
        <v-text-field
          v-model.number="localValue.repeatEvery"
          type="number"
          :rules="repeatEveryRules"
          :label="
            localValue.repeatEvery ? $t('devicesSettings.devicePolicyTab.form.repeatEvery') : ''
          "
          :aria-label="$t('devicesSettings.devicePolicyTab.form.repeatEvery')"
          :placeholder="$t('devicesSettings.devicePolicyTab.form.repeatEvery')"
          variant="outlined"
          data-testid="fim-repeat-every"
        />
      </v-col>
      <v-col class="value-part" cols="6" md="6">
        <v-select
          v-model="localValue.intervalUnit"
          :rules="[required()]"
          :label="
            localValue.intervalUnit
              ? $t('devicesSettings.devicePolicyTab.form.intervalUnitLabel')
              : ''
          "
          :placeholder="$t('general.select')"
          :items="intervalUnitOptions"
          variant="outlined"
          data-testid="fim-interval-unit"
        >
          <template #selection="data">
            {{ data.item.title }}
          </template>
          <template #item="{ props, item }">
            <v-list-item v-bind="props" :title="item.title"></v-list-item>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <div class="subtitle1">{{ $t("devicesSettings.devicePolicyTab.form.monitoredFilePath") }}</div>
    <div class="body2 text-indigo-medium mb-4">
      {{ $t("devicesSettings.devicePolicyTab.form.monitoredFilePathHelper") }}
    </div>
    <div data-testid="fim-paths-input">
      <div class="w-100">
        <div
          v-for="(_, index) in localValue.monitoredPaths"
          :key="index"
          class="d-flex align-center mb-2"
        >
          <v-text-field
            v-model="localValue.monitoredPaths[index]"
            variant="outlined"
            :rules="pathRules"
            :label="$t('devicesSettings.devicePolicyTab.form.filePath')"
            :placeholder="pathPlaceholder"
            :data-testid="`fim-path-${index}`"
          />
          <v-icon
            icon="$trash"
            size="24"
            class="ml-2 item-clickable"
            :disabled="localValue.monitoredPaths.length === 1"
            :data-testid="`fim-path-remove-${index}`"
            @click="removePath(index)"
          />
        </div>
        <a
          v-if="localValue.monitoredPaths.length < MAX_MONITORED_PATHS"
          class="coro-link"
          data-testid="fim-add-path"
          @click="addPath"
        >
          + {{ $t("devicesSettings.devicePolicyTab.form.addPath") }}
        </a>
      </div>
    </div>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { OsType } from "@/constants/devices";
import { FileIntegrityAction, IntervalUnit } from "@/constants/device-policy";
import { Patterns } from "@coronet/cui";
import { minNumber, numberRequired, onlyInteger, required } from "@/_helpers/validators";
import type { DevicePolicyFormModel } from "@/constants/device-policy";
import type { VuetifyFormRef } from "@/types";

const MAX_MONITORED_PATHS = 5;

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DevicePolicyFormModel>,
      required: true,
    },
  },
  emits: ["update:config"],
  setup(props, { emit }) {
    const i18n = useI18n();
    // Owns only the fields this sub-form edits; policyType/labels stay with the parent and are
    // re-read from props on every emit so they can never be clobbered by a stale copy.
    const localValue = ref({
      os: props.config.os ?? OsType.WINDOWS,
      repeatEvery: props.config.repeatEvery ?? 1,
      intervalUnit: props.config.intervalUnit ?? IntervalUnit.HOURS,
      monitoredPaths: [...(props.config.monitoredPaths ?? [""])],
      action: FileIntegrityAction.REVIEW,
    });

    watch(
      localValue,
      (value) =>
        emit("update:config", {
          ...props.config,
          ...value,
          monitoredPaths: [...value.monitoredPaths],
        }),
      { deep: true }
    );

    const valid = ref(true);
    const form = ref<VuetifyFormRef>();

    const osOptions = [
      { value: OsType.WINDOWS, title: i18n.t("devicesSettings.devicePolicyTab.osTypes.WINDOWS") },
      { value: OsType.OSX, title: i18n.t("devicesSettings.devicePolicyTab.osTypes.OSX") },
    ];
    const intervalUnitOptions = Object.values(IntervalUnit).map((unit) => ({
      value: unit,
      title: i18n.t(`devicesSettings.devicePolicyTab.form.intervalUnits.${unit}`),
    }));
    const actionOptions = Object.values(FileIntegrityAction).map((value) => ({
      value,
      title: i18n.t(`devicesSettings.devicePolicyTab.form.actions.${value}`),
    }));

    const repeatEveryRules = [
      numberRequired(),
      onlyInteger(),
      minNumber(1, i18n.t("validations.onlyPositiveNumbers")),
    ];

    const windowsServerHint = computed(() =>
      localValue.value.os === OsType.WINDOWS
        ? i18n.t("devicesSettings.devicePolicyTab.form.windowsServerNote")
        : ""
    );

    const pathPlaceholder = computed(() =>
      localValue.value.os === OsType.OSX
        ? i18n.t("devicesSettings.devicePolicyTab.form.pathExampleMacOs")
        : i18n.t("devicesSettings.devicePolicyTab.form.pathExampleWindows")
    );

    const validPath = (value: string) => {
      const isValid =
        localValue.value.os === OsType.OSX ? Patterns.MAC_FILE_PATH : Patterns.WINDOWS_FILE_PATH;
      if (isValid(value)) return true;
      return localValue.value.os === OsType.OSX
        ? i18n.t("validations.invalidMacPath")
        : i18n.t("validations.invalidWindowsPath");
    };

    const pathRules = computed(() => [required(), validPath]);

    const validate = async () => {
      const result = await form.value?.validate();
      return result ? result.valid : true;
    };

    const addPath = () => {
      if (localValue.value.monitoredPaths.length >= MAX_MONITORED_PATHS) return;
      localValue.value.monitoredPaths.push("");
    };
    const removePath = (index: number) => {
      if (localValue.value.monitoredPaths.length === 1) return;
      localValue.value.monitoredPaths.splice(index, 1);
    };

    return {
      MAX_MONITORED_PATHS,
      localValue,
      actionOptions,
      valid,
      form,
      osOptions,
      intervalUnitOptions,
      repeatEveryRules,
      windowsServerHint,
      pathPlaceholder,
      required,
      pathRules,
      validate,
      addPath,
      removePath,
    };
  },
});
</script>

<style lang="scss" scoped>
// Joined two-field row, lifted from KeyValueInput so the interval pair reads as one control.
:deep(.key-part) {
  .v-field {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .v-field__outline__end {
    border-right: none;
  }

  .v-field:hover {
    .v-field__outline__end {
      border-right: 1px solid rgb(var(--v-theme-primary));
    }
  }

  .v-field--error {
    .v-field__outline__end {
      border-right: 2px solid rgb(var(--v-theme-red-dark));
    }
  }

  .v-field--focused:not(.v-field--error) {
    .v-field__outline__end {
      border-right: 2px solid rgb(var(--v-theme-primary));
    }
  }
}

:deep(.value-part) {
  .v-field {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}

:deep(*) {
  .key-part {
    padding: 0 0 0 12px;

    .error--text fieldset {
      border-right: 1px solid rgb(var(--v-theme-red-dark)) !important;
    }

    .v-input--is-focused:not(.error--text) fieldset {
      border-right-width: 1px solid rgb(var(--v-theme-primary)) !important;
    }
  }
  .value-part {
    padding: 0 12px 0 0;
  }
}
</style>
